import React, { useState } from 'react'
import { makeStyles, createStyles, Typography, Dialog } from '@material-ui/core'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import InStoreReturnLink from '../InStore/InStoreLink'
import Button from '../Button/Button'
import ErrorIcon from '@material-ui/icons/Error'
import CloseIcon from '@material-ui/icons/Close'
import CMSText from '../Global/CMSText'
import { isSubscription } from '../../utils/constants'
import DownloadInvoiceButton from './DownloadInvoiceButton/DownloadInvoiceButton'
import RentalReturnLabelDownload from './RentalReturnLabelDownload'
import { useTakeback } from '../../hooks/useTakeback'
import { ORDER_ITEM_TYPE } from '../../utils/constants'

const isBuyingOption = process.env.BUYING_OPTION

const useStyles = makeStyles((theme) =>
  createStyles({
    orderListTitle: {
      marginBottom: theme.spacing(2),
    },
    orderHeader: {
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'center',
    },
    circleDefault: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: theme.palette.common.black,
    },
    circlePrimary: {
      backgroundColor: theme.palette.primary.main,
    },
    circleSecondary: {
      backgroundColor: theme.palette.secondary.main,
    },
    circleWarning: {
      backgroundColor: theme.palette.warning.main,
    },
    orderTitle: {
      paddingLeft: theme.spacing(1),
    },
    orderItem: {
      borderLeft: `1px solid currentColor`,
      paddingLeft: theme.spacing(2),
      marginLeft: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    orderItemComing: {
      borderLeft: `1px dashed ${theme.extraColors.greyMedium}`,
    },
    orderItemReturn: {
      borderLeft: `1px solid ${theme.palette.secondary.main}`,
    },
    orderItemCancelled: {
      borderLeft: `1px dashed ${theme.palette.warning.main}`,
    },
    orderItemLast: {
      border: 0,
    },
    productCard: {
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      minHeight: 60,
      padding: theme.spacing(1),
      display: 'flex',
      justifyContent: '',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    imgProduct: {
      width: 50,
      height: 50,
      objectFit: 'contain',
      display: 'block',
    },
    imgContainer: {
      width: 50,
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(0),
      },
    },
    productName: {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(1),
      },
    },
    downloadInvoiceButton: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    warning: {
      color: theme.palette.warning.main,
    },
    buyArea: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    cancelArea: {
      textAlign: 'right',
      marginLeft: '1rem',
      paddingBottom: '0.3rem',
    },
    cancelBtn: {
      padding: 0,
      textTransform: 'none',
      textDecoration: 'underline',
      fontSize: '80%',
      color: theme.extraColors.greyText,
      '&:hover, &:focus': {
        background: 'none',
      },
    },
    modal: {
      borderRadius: 0,
      '& .MuiPaper-root': {
        borderRadius: 0,
        [theme.breakpoints.up('md')]: {
          minWidth: 700,
        },
      },
    },
    modalHead: {
      padding: theme.spacing(2, 3),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: theme.extraColors.greyLight,
      '& .MuiTypography-root': {
        fontWeight: 400,
        lineHeight: 1,
      },
    },
    modalHeadWarning: {
      display: 'inline-flex',
      alignItems: 'center',
      color: theme.palette.error.main,
      '&>.MuiSvgIcon-root': {
        marginRight: theme.spacing(1),
      },
    },
    modalFoot: {
      padding: theme.spacing(1, 2),
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    modalContent: {
      padding: theme.spacing(3),
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    modalClose: {
      cursor: 'pointer',
      padding: theme.spacing(0.5),
      width: theme.spacing(4),
      height: theme.spacing(4),
      borderRadius: theme.spacing(2),
      textAlign: 'center',
      color: theme.extraColors.greyLight,
      background: theme.extraColors.greyMedium,
      '&:hover, &:focus': {
        background: theme.palette.common.black,
      },
    },
    cancelCTA: {
      // color: theme.palette.error.main,
      // borderColor: theme.palette.error.main,
    },
  })
)

type Tprops = {
  doc: any
  t: any
  orders: any
  inStoreText: any
  isInStore: boolean | undefined
  showSizes: boolean
  putUserOrder: any
  getUserOrders: any
  takeback: any
  isSecondHand?: boolean
  unit?: any
  handleDownloadInvoice?: (orderNumber: string) => any
  invoiceDownloadError: boolean
}

const OrderList = ({
  orders,
  doc,
  t,
  isInStore,
  inStoreText,
  showSizes,
  putUserOrder,
  getUserOrders,
  takeback,
  isSecondHand,
  unit,
  handleDownloadInvoice,
  invoiceDownloadError,
}: Tprops) => {
  const classes = useStyles()
  const { trackEvent } = useMatomo()
  const { productCheckState } = useTakeback()

  const [openModal, setOpenModal] = useState({
    open: false,
    currentOrder: null,
  })
  const states = productCheckState(takeback?.data)
  const noCalendly = process.env.NO_CALENDLY
  const handleOpen = (currentOrder) => {
    setOpenModal({ open: true, currentOrder: currentOrder })
  }

  const handleClose = () => {
    setOpenModal({ open: false, currentOrder: null })
  }

  const handleCancel = (order) => {
    const cancelOrder = async () => {
      await setOpenModal({ open: false, currentOrder: null })
      const response = await putUserOrder(order.tokenValue)
      if (response.type === 'PUT_USER_ORDER_SUCCESS') {
        await getUserOrders()
      }
    }
    cancelOrder()
  }

  return (
    <>
      <Typography
        variant={'h5'}
        component={'h2'}
        className={classes.orderListTitle}
      >
        <CMSText
          asText
          data={doc?.data?.order_list_title}
          defaultText={t('texts:account:order_list_title')}
        />
      </Typography>
      {orders?.length > 0 &&
        orders.map((order, index) => {
          const orderNumber = `order--${order.number}`
          const isCancelled = order.shipments[0].state === 'cancelled'
          const lastOrder = index + 1 === orders.length
          const isRental = order?.items?.[0]?.type === ORDER_ITEM_TYPE.RENTAL
          let state: string
          if (isCancelled) {
            state = 'cancelled'
          } else {
            if (
              dayjs().diff(
                dayjs(order.startLeasingDate, 'DD/MM/YYYY'),
                'day'
              ) <= 0
            ) {
              state = 'coming'
            } else {
              if (
                dayjs().diff(
                  dayjs(order.endLeasingDate, 'DD/MM/YYYY'),
                  'day'
                ) <= 0
              ) {
                state = 'return'
              } else {
                state = 'finished'
              }
            }
          }

          return (
            <div
              key={orderNumber}
              className={clsx('order', orderNumber)}
              id={orderNumber}
            >
              <div className={`order--${state}`}>
                <div className={classes.orderHeader}>
                  {state === 'cancelled' && (
                    <div
                      className={clsx(
                        classes.circleDefault,
                        classes.circleWarning
                      )}
                    ></div>
                  )}
                  {state === 'coming' && (
                    <div
                      className={clsx(
                        classes.circleDefault,
                        classes.circlePrimary
                      )}
                    ></div>
                  )}
                  {state === 'return' && (
                    <div
                      className={clsx(
                        classes.circleDefault,
                        classes.circleSecondary
                      )}
                    ></div>
                  )}
                  {state === 'finished' && (
                    <div className={classes.circleDefault}></div>
                  )}
                  <div className={classes.orderTitle}>
                    {state === 'cancelled' && (
                      <Typography
                        variant={'body1'}
                        color={'inherit'}
                        className={classes.warning}
                      >
                        {t('texts:account:cancelled')}
                        <b>
                          &nbsp;-&nbsp;
                          {order.cancelledAt
                            ? order.cancelledAt
                            : order.startLeasingDate}
                        </b>
                      </Typography>
                    )}
                    {state === 'coming' && (
                      <Typography variant={'body1'} color={'primary'}>
                        <CMSText
                          asText
                          data={doc?.data?.date}
                          defaultText={t('texts:account:date')}
                        />
                        &nbsp;
                        <b>
                          {dayjs(order.checkoutCompletedAt).format(
                            'DD/MM/YYYY'
                          )}
                        </b>
                      </Typography>
                    )}
                    {state === 'return' && (
                      <Typography variant={'body1'} color={'secondary'}>
                        <CMSText
                          asText
                          data={doc?.data?.date}
                          defaultText={t('texts:account:date')}
                        />
                        &nbsp;
                        <b>{order.endLeasingDate}</b>
                      </Typography>
                    )}
                    {state === 'finished' && (
                      <Typography variant={'body1'} color={'textPrimary'}>
                        <CMSText
                          asText
                          data={doc?.data?.returned_at}
                          defaultText={t('texts:account:returned_at')}
                        />
                        &nbsp;
                        {order.endLeasingDate}
                      </Typography>
                    )}
                  </div>
                </div>
                <div
                  className={clsx(
                    ['order__item', classes.orderItem],
                    state === 'cancelled' && classes.orderItemCancelled,
                    state === 'coming' && classes.orderItemComing,
                    state === 'return' && classes.orderItemReturn,
                    state === 'finished' && classes.orderItem,
                    lastOrder && ['order__item--last', classes.orderItemLast]
                  )}
                >
                  <div>
                    <div className={classes.orderHeader}>
                      <Typography variant={'body2'} color={'textSecondary'}>
                        <CMSText
                          asText
                          data={doc?.data?.command}
                          defaultText={t('texts:account:command')}
                        />
                        &nbsp;
                        {order.number}
                      </Typography>
                      {order?.isCancellable && (
                        <div
                          className={clsx('order-cancel', classes.cancelArea)}
                        >
                          <Button
                            size="small"
                            disableRipple
                            className={clsx(
                              'order-cancel__btn',
                              classes.cancelBtn
                            )}
                            onClick={() => handleOpen(order.number)}
                          >
                            <CMSText
                              asText
                              data={doc?.data?.cancel_order_cta_modal}
                              defaultText={t(
                                'texts:account:cancel_order:cta_modal'
                              )}
                            />
                          </Button>
                        </div>
                      )}
                    </div>
                    {isRental && (
                      <div>
                        <Typography variant={'body2'} color={'textSecondary'}>
                          <CMSText
                            asText
                            data={doc?.data?.leasing_date_from}
                            defaultText={t('texts:account:leasing_date_from')}
                          />
                          &nbsp;
                          <b>{order.startLeasingDate}</b>
                          &nbsp;
                          <CMSText
                            asText
                            data={doc?.data?.leasing_date_to}
                            defaultText={t('texts:account:leasing_date_to')}
                          />
                          &nbsp;
                          <b>{order.endLeasingDate}</b>
                        </Typography>
                      </div>
                    )}
                    <Typography variant={'body2'}>
                      {`${order.shippingAddress.street} ${
                        order.shippingAddress.city
                      } ${
                        order.shippingAddress.postcode === '00000'
                          ? ''
                          : order.shippingAddress.postcode
                      } ${order.shippingAddress.countryCode}`}
                    </Typography>
                  </div>
                  {order?.items.map((item, index) => {
                    return (
                      <div key={item.product.name + index}>
                        <div className={classes.productCard}>
                          <div className={classes.imgContainer}>
                            <img
                              src={
                                item.product.images.length > 0 &&
                                item.product?.images?.[0]?.cachedPath
                              }
                              alt={item.product.name}
                              className={classes.imgProduct}
                            />
                          </div>
                          <div className={classes.productName}>
                            <Typography variant={'h5'}>
                              {item.product.name}
                              {showSizes &&
                                item.product.variants !== 'undefined' && (
                                  <>
                                    {item?.product?.variants[0]?.size && (
                                      <>&nbsp;-&nbsp;</>
                                    )}
                                    <Typography
                                      variant={'body1'}
                                      component="span"
                                    >
                                      {item?.product?.variants[0]?.size}
                                    </Typography>
                                    {isSecondHand && (
                                      <>
                                        &nbsp;-&nbsp;
                                        <Typography
                                          variant={'body1'}
                                          component="span"
                                        >
                                          {
                                            states[`${unit.productCondition}`]
                                              .title
                                          }
                                        </Typography>
                                      </>
                                    )}
                                  </>
                                )}
                            </Typography>
                          </div>
                        </div>
                        {!noCalendly && (
                          <InStoreReturnLink
                            isInStore={isInStore}
                            cmsDefaultText={t(
                              'texts:instore:account_page_return_link_label'
                            )}
                            cmsText={
                              inStoreText?.data?.account_page_return_link_label
                            }
                          />
                        )}
                        <div
                          className={clsx(
                            'order__buy',
                            isBuyingOption === 'true' &&
                              state === 'return' &&
                              classes.buyArea
                          )}
                        >
                          {isBuyingOption === 'true' && state === 'return' && (
                            <>
                              <Button
                                variant={'contained'}
                                color={'secondary'}
                                size={'small'}
                                onClick={() =>
                                  trackEvent({
                                    category: 'Buying option click',
                                    action: `User clicked on the buying option button`,
                                  })
                                }
                              >
                                {t('texts:account:buy')}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  <RentalReturnLabelDownload
                    orderNumber={order.number}
                    account={doc}
                    t={t}
                  />
                  {!!order?.invoice?.id && (
                    <div className={classes.downloadInvoiceButton}>
                      <DownloadInvoiceButton
                        accountRes={doc}
                        orderToken={order.tokenValue}
                        t={t}
                        handleDownloadInvoice={handleDownloadInvoice}
                        invoiceDownloadError={invoiceDownloadError}
                        isSub={isSubscription}
                      />
                    </div>
                  )}
                </div>
              </div>
              {order?.isCancellable && (
                <Dialog
                  className={clsx('modal-cancelOrder', classes.modal)}
                  open={openModal.currentOrder === order.number}
                  onClose={handleClose}
                  maxWidth={'md'}
                  disableBackdropClick
                >
                  <div
                    className={clsx(
                      'modal-cancelOrder__head',
                      classes.modalHead
                    )}
                  >
                    <Typography variant="body1" component={'h2'}>
                      <span
                        className={clsx(
                          'modal-cancelOrder__warning',
                          classes.modalHeadWarning
                        )}
                      >
                        <ErrorIcon />
                        <span>
                          <CMSText
                            asText
                            data={doc?.data?.cancel_order_modal_title}
                            defaultText={t(
                              'texts:account:cancel_order:modal_title'
                            )}
                          />
                        </span>
                      </span>
                    </Typography>
                    <CloseIcon
                      className={clsx(
                        'modal-cancelOrder__close',
                        classes.modalClose
                      )}
                      onClick={handleClose}
                    />
                  </div>
                  <div
                    className={clsx(
                      'modal-cancelOrder__content',
                      classes.modalContent
                    )}
                  >
                    <Typography variant={'h4'} component={'h3'}>
                      <CMSText
                        asText
                        data={doc?.data?.command}
                        defaultText={t('texts:account:command')}
                      />
                      {order.orderNumber || order.number}
                    </Typography>
                    <Typography component={'div'}>
                      <CMSText
                        asText
                        data={doc?.data?.cancel_order_modal_warn}
                        defaultText={t('texts:account:cancel_order:modal_warn')}
                      />
                    </Typography>
                    <Button
                      color={'primary'}
                      variant={'outlined'}
                      size={'small'}
                      className={clsx(
                        'modal-cancelOrder__cta',
                        classes.cancelCTA
                      )}
                      onClick={() => handleCancel(order)}
                    >
                      <CMSText
                        asText
                        data={doc?.data?.cancel_order_modal_cta}
                        defaultText={t('texts:account:cancel_order:modal_cta')}
                      />
                    </Button>
                  </div>
                </Dialog>
              )}
            </div>
          )
        })}
    </>
  )
}

export default OrderList
